import React, { useState, useEffect } from 'react';
import { WelcomeCardConfig, CategoryType, QuickActionTemplate } from './types';
import { QuickActionGrid } from './QuickActionGrid';
import styles from './WelcomeScreen.module.css';
import { CardType } from '../../store/quickActionsStore';
import useQuickActionsStore from '../../store/quickActionsStore';
import { trackEvent } from '../../services/analyticsService';
import { logDebug } from '../../utils/logging';

// Welcome message configuration from the plan
const WELCOME_CONFIG = {
  title: "Salutări, Smartie aici, asistentul AI SMARTERS.",
  subtitle: "Ce provocări de marketing încerci să rezolvi astăzi?",
  cards: [
    {
      id: 'creaza_draft',
      title: 'Creează un draft',
      description: 'Generează copy pentru marketing și documente',
      icon: '📝',
      cardType: 'creaza_draft' as const,
      action: 'show_categories' as const
    },
    {
      id: 'obtine_sfaturi',
      title: 'Obține sfaturi strategice', 
      description: 'Consultanță strategică pentru afacerea ta',
      icon: '💡',
      cardType: 'sfaturi_strategice' as const,
      action: 'show_categories' as const
    },
    /*
    {
      id: 'sugereste_curs',
      title: 'Sugerează un curs',
      description: 'Recomandări de cursuri pentru dezvoltare',
      icon: '📚',
      cardType: 'sugereste_curs' as const,
      action: 'course_flow' as const
    }
    */
  ]
};

interface WelcomeScreenProps {
  onActionSelect: (cardType: string, category?: CategoryType, template?: QuickActionTemplate) => void;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onActionSelect }) => {
  const [selectedCard, setSelectedCard] = useState<WelcomeCardConfig | null>(null);
  const [showCategories, setShowCategories] = useState(false);

  // Get template loading state from store
  const { 
    isLoading: isLoadingTemplates, 
    error: templateError, 
    loadTemplates, 
    clearError,
    isInitialized 
  } = useQuickActionsStore();

  // Load templates on component mount
  useEffect(() => {
    if (!isInitialized) {
      logDebug('[WelcomeScreen] Loading templates on startup...');
      loadTemplates().catch((error) => {
        logDebug('[WelcomeScreen] Error loading templates:', error);
      });
      
      // Track welcome screen initialization
      trackEvent('welcome_screen_initialized');
    }
  }, [isInitialized, loadTemplates]);

  const handleCardClick = (card: WelcomeCardConfig) => {
    logDebug('[WelcomeScreen] Card clicked:', card.cardType);
    
    // Track card interaction
    trackEvent('welcome_card_clicked', {
      card_type: card.cardType,
      action: card.action
    });

    if (card.action === 'show_categories') {
      setSelectedCard(card);
      setShowCategories(true);
    } else {
      // Direct actions for strategic advice or course suggestions
      onActionSelect(card.cardType);
    }
  };

  const handleTemplateSelect = (template: QuickActionTemplate) => {
    logDebug('[WelcomeScreen] Template selected:', template.template_id);
    
    if (selectedCard) {
      onActionSelect(selectedCard.cardType, undefined, template);
    }
  };

  const handleBackToCards = () => {
    logDebug('[WelcomeScreen] Back to cards clicked');
    setSelectedCard(null);
    setShowCategories(false);
    
    // Clear any template loading errors when going back
    if (templateError) {
      clearError();
    }
  };

  // Show loading state if templates are loading on first load
  if (isLoadingTemplates && !isInitialized) {
    return (
      <div className={styles.welcomeScreen}>
        <div className={styles.welcomeHeader}>
          <h1 className={styles.welcomeTitle}>{WELCOME_CONFIG.title}</h1>
          <p className={styles.welcomeSubtitle}>Se încarcă opțiunile...</p>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>Se pregătesc opțiunile pentru tine...</p>
        </div>
      </div>
    );
  }

  // Show error state if template loading failed
  if (templateError && !isInitialized) {
    return (
      <div className={styles.welcomeScreen}>
        <div className={styles.welcomeHeader}>
          <h1 className={styles.welcomeTitle}>{WELCOME_CONFIG.title}</h1>
          <p className={styles.welcomeSubtitle}>A apărut o problemă...</p>
        </div>
        <div className={styles.errorState}>
          <p className={styles.errorMessage}>
            Ne pare rău, nu am putut încărca opțiunile disponibile.
          </p>
          <button 
            className={styles.retryButton}
            onClick={() => {
              logDebug('[WelcomeScreen] Retry button clicked');
              clearError();
              loadTemplates();
            }}
          >
            Încearcă din nou
          </button>
        </div>
      </div>
    );
  }

  if (showCategories && selectedCard) {
    return (
      <div className={styles.welcomeScreen}>
        <div className={styles.welcomeHeader}>
          <button 
            className={styles.backButton}
            onClick={handleBackToCards}
            aria-label="Înapoi la opțiuni principale"
          >
            ← Înapoi
          </button>
          <h2 className={styles.categoryTitle}>
            <span className={styles.categoryIcon}>{selectedCard.icon}</span>
            {selectedCard.title}
          </h2>
          <p className={styles.categorySubtitle}>Alege categoria care te interesează:</p>
        </div>
        <QuickActionGrid 
          onTemplateSelect={handleTemplateSelect}
          cardType={selectedCard.cardType as CardType}
        />
      </div>
    );
  }

  return (
    <div className={styles.welcomeScreen}>
      <div className={styles.welcomeHeader}>
        <h1 className={styles.welcomeTitle}>{WELCOME_CONFIG.title}</h1>
        <p className={styles.welcomeSubtitle}>{WELCOME_CONFIG.subtitle}</p>
      </div>
      
      <div className={styles.cardGrid}>
        {WELCOME_CONFIG.cards.map((card) => (
          <button
            key={card.id}
            className={styles.actionCard}
            onClick={() => handleCardClick(card)}
            aria-label={`${card.title} - ${card.description}`}
          >
            <div className={styles.cardIcon}>{card.icon}</div>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>{card.title}</h3>
              <p className={styles.cardDescription}>{card.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}; 